import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Box, Pagination, Skeleton } from '@mui/material'
import { observer } from 'mobx-react-lite'

import { useStore } from '@hooks/use-store'
import { ISearchResult } from '@typings'

import { AcSearchResult } from '../ac-search-result/ac-search-result'

export const AcSearchResults = observer(() => {
  const navigate = useNavigate()
  const { search } = useStore()

  const handlePaginationChange = async (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    search.setPage(value)
    await search.search()
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  // Use backend sorting - results are already sorted by:
  // 1. hit_count (desc), 2. track name (asc), 3. milestone position (asc), 4. name (asc)
  const sortedResults = useMemo(() => {
    if (!search.current_items || search.current_items.length === 0) {
      return []
    }
    return search.current_items
  }, [search.current_items])

  const renderSearchResults = useMemo(() => {
    if (search.is_loading) {
      // return 7 skeletons
      return Array.from({ length: 7 }).map((_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          height={210}
        />
      ))
    }

    if (sortedResults.length <= 0 && !search.is_loading) {
      return (
        <Box
          sx={{
            padding: '24px',
            display: 'flex',
            justifyContent: 'center',
          }}>
          <Alert
            color="warning"
            severity="warning">
            {'Er zijn geen zoekresultaten gevonden met de gekozen filters.'}
          </Alert>
        </Box>
      )
    }

    return sortedResults.map((item: ISearchResult) => (
      <AcSearchResult
        key={item.id}
        {...item}
      />
    ))
  }, [sortedResults, search.is_loading])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}>
      {renderSearchResults}
      <Pagination
        hidePrevButton
        hideNextButton
        count={search.meta?.last_page}
        page={search.current_page}
        color="primary"
        onChange={handlePaginationChange}
      />
    </Box>
  )
})
